import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResource } from "Translations/Resources";
import {
  type CampaignFormData,
  getCampaignFormSchema,
} from "./campaignFormSchema";
import { useState } from "react";
import { generateCampaignCode } from "Utils/CampaignCodeGenerator";
import type { AdminCampaignDto } from "Infrastructure/Api/Api";
import { Stepper } from "Components/Shared/Stepper/Stepper";
import { BasicInfoStep } from "./BasicInfoStep";
import { LandingPageStep } from "./LandingPageStep";
import { ThankYouPageStep } from "./ThankYouPageStep";
import { ValidityStep } from "./ValidityStep";
import { StepNavigation } from "./StepNavigation";
import { type CampaignStep, useCampaignStepper } from "./useCampaignStepper";

type Props = {
  defaultValues?: Partial<CampaignFormData>;
  onSubmit: (data: CampaignFormData) => Promise<void>;
  onPublish?: (data: CampaignFormData) => Promise<void>;
  onArchive?: () => void;
  onUnpublish?: () => void;
  isSubmitting: boolean;
  isEditMode?: boolean;
  campaign?: AdminCampaignDto;
  activeStep?: CampaignStep;
  onStepChange?: (step: CampaignStep) => void;
};

export type { CampaignStep };

export const CampaignForm = ({
  defaultValues,
  onSubmit,
  onPublish,
  onArchive,
  onUnpublish,
  isSubmitting,
  isEditMode = false,
  campaign,
  activeStep = "basic-info",
  onStepChange,
}: Props) => {
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const { t } = useResource();

  const {
    steps,
    activeStepIndex,
    handleStepClick,
    isFirstStep,
    isLastStep,
    currentFormId,
    advanceStep,
    goToPreviousStep,
  } = useCampaignStepper({ activeStep, isEditMode, onStepChange });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState,
    reset,
    clearErrors,
  } = useForm<CampaignFormData>({
    resolver: zodResolver(getCampaignFormSchema(t)),
    defaultValues: {
      isAffiliateEnabled: false,
      ...defaultValues,
      dateMode:
        defaultValues?.dateMode ??
        (defaultValues?.startDate || defaultValues?.endDate
          ? "from-to"
          : "unlimited"),
    },
  });

  const dateMode = watch("dateMode");

  const handleNameChange = () => {
    const currentName = getValues("name");
    if (!isEditMode && currentName && !codeManuallyEdited) {
      const generatedCode = generateCampaignCode(currentName);
      setValue("code", generatedCode);
    }
  };

  const handleDateModeChange = (value: string) => {
    if (value === "unlimited") {
      setValue("startDate", null);
      setValue("endDate", null);
    }
    clearErrors(["startDate", "endDate"]);
  };

  const handleFormSubmit = async (data: CampaignFormData) => {
    await onSubmit(data);
    reset(data);
    advanceStep();
  };

  const handleSaveOnly = async (data: CampaignFormData) => {
    await onSubmit(data);
    reset(data);
  };

  const handleSaveAndPublish = handleSubmit(async (data) => {
    await onPublish?.(data);
    reset(data);
  });

  const campaignId = campaign?.campaignID;
  const formOnSubmit = handleSubmit(handleFormSubmit);

  return (
    <Stack gap={4}>
      {isEditMode && (
        <Stepper
          nonLinear
          steps={steps}
          activeStepIndex={activeStepIndex}
          onStepClick={handleStepClick}
          sx={{ width: "100%" }}
        />
      )}

      {activeStep === "basic-info" && (
        <BasicInfoStep
          formId={currentFormId}
          onSubmit={formOnSubmit}
          control={control}
          isSubmitting={isSubmitting}
          isEditMode={isEditMode}
          campaign={campaign}
          onCodeManuallyEdited={() => setCodeManuallyEdited(true)}
          onNameChange={handleNameChange}
        />
      )}

      {isEditMode && campaignId != null && (
        <>
          {activeStep === "landing-page" && (
            <LandingPageStep
              formId={currentFormId}
              onSubmit={formOnSubmit}
              control={control}
              watch={watch}
              isSubmitting={isSubmitting}
              campaignId={campaignId}
            />
          )}

          {activeStep === "thank-you-page" && (
            <ThankYouPageStep
              formId={currentFormId}
              onSubmit={formOnSubmit}
              control={control}
              watch={watch}
              isSubmitting={isSubmitting}
              campaignId={campaignId}
            />
          )}

          {activeStep === "validity" && (
            <ValidityStep
              formId={currentFormId}
              onSubmit={formOnSubmit}
              control={control}
              isSubmitting={isSubmitting}
              campaign={campaign}
              dateMode={dateMode}
              onDateModeChange={handleDateModeChange}
              onPublish={onPublish ? handleSaveAndPublish : undefined}
              onArchive={onArchive}
              onUnpublish={onUnpublish}
            />
          )}

          <StepNavigation
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isDirty={formState.isDirty}
            isSubmitting={isSubmitting}
            formId={currentFormId}
            onPrevious={goToPreviousStep}
            onAdvance={advanceStep}
            onSaveOnly={handleSubmit(handleSaveOnly)}
          />
        </>
      )}
    </Stack>
  );
};
